import React, { useEffect, useState, useTransition } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { setAuthToken, setWorkspaceId, getWorkspaceId, apiClient } from '@/services/api';
import { integrationsApi } from '@/services/api/integrationApi';
import { rememberShopifyHandoff } from '../lib/shopifyHandoff';
import { startGoogleSignIn } from '../lib/googleSignIn';
import { forgetGoogleSignInRedirect, rememberGoogleSignInRedirect, safeRedirect } from '../app/loginRedirect';
import { pickWorkspaceId } from '@/lib/workspaceSelect';
import { storageSet } from '@/lib/nativeStorage';
import { isNativePlatform, nativePlatform } from '@/lib/nativeApp';
import { startNativeGoogleLogin, startNativeAppleLogin } from '@/lib/nativeAuth';
import { AuthFormCard, AuthPageLayout } from '@/layouts/AuthPageLayout';
import { shopifyConnectedDestination } from '../lib/shopifyConnectedDestination';
import { ShopifyReconnectNotice } from '../components/auth/ShopifyReconnectNotice';
import api from '../services/api';
import { toast } from '../stores/toastStore';
import { t } from '@/i18n';
import {
  AuthButton,
  AuthCheckbox,
  AuthDivider,
  AuthGoogleButton,
  AuthInput,
  AuthLabel,
  FieldError,
} from './auth/authFormControls';
import { validateEmail, validateLoginPassword } from './auth/authValidation';
import { isTwoFactorRequired } from './auth/isTwoFactorRequired';
import { TwoFactorVerifyModal } from './auth/TwoFactorVerifyModal';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface LoginSuccessPayload {
  token: string;
  refreshToken?: string;
  workspaces?: Array<{ id: string }>;
}

type LoginField = 'email' | 'password';
type FieldErrors = Partial<Record<LoginField, string>>;

function validateLogin(data: LoginFormData): FieldErrors {
  const next: FieldErrors = {};
  const emailErr = validateEmail(data.email);
  const passwordErr = validateLoginPassword(data.password);
  if (emailErr) next.email = emailErr;
  if (passwordErr) next.password = passwordErr;
  return next;
}

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectParam = searchParams.get('redirect');
  const redirectTo = safeRedirect(redirectParam);
  const oauthError = searchParams.get('error');
  useEffect(() => {
    if (oauthError) {
      forgetGoogleSignInRedirect();
      toast.error('Sign-in failed. Please try again.');
    }
  }, [oauthError]);
  const shopifyShop = searchParams.get('shopify') === 'connected' ? searchParams.get('shop') : null;
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [needsTwoFactor, setNeedsTwoFactor] = useState(false);
  const [twoFactorError, setTwoFactorError] = useState<string | null>(null);
  const [twoFactorBusy, setTwoFactorBusy] = useState(false);
  const [, startTransition] = useTransition();

  /** Clear a field's error as soon as the user starts correcting it. */
  const clearFieldError = (field: LoginField): void => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const { [field]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const completeLogin = async (payload: LoginSuccessPayload): Promise<void> => {
    setAuthToken(payload.token);
    if (payload.refreshToken) {
      storageSet('buzzbit_refresh_token', payload.refreshToken);
    }

    setWorkspaceId(pickWorkspaceId(getWorkspaceId(), payload.workspaces ?? []));

    toast.success('Signed in successfully.');

    if (shopifyShop) {
      let target = '/account?tab=integrations&shopify=error&reason=finalize_failed';
      try {
        await integrationsApi.shopifyFinalize(shopifyShop);
        target = await shopifyConnectedDestination(() => api.settings.getBrand());
      } catch {
        // target already names the failure
      }
      startTransition(() => { navigate(target); });
      return;
    }

    startTransition(() => { navigate(redirectTo); });
  };

  const attemptLogin = async (totpCode?: string): Promise<void> => {
    const body: { email: string; password: string; totpCode?: string } = {
      email: formData.email.trim(),
      password: formData.password,
    };
    if (totpCode) body.totpCode = totpCode;

    const response = await apiClient.post<{
      success: boolean;
      data: LoginSuccessPayload;
    }>('/auth/login', body);

    const payload = response.data ?? response;
    await completeLogin(payload);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const nextErrors = validateLogin(formData);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setIsLoading(true);
    setTwoFactorError(null);

    try {
      await attemptLogin();
    } catch (err) {
      if (isTwoFactorRequired(err)) {
        setNeedsTwoFactor(true);
        setIsLoading(false);
        return;
      }
      const msg = err instanceof Error ? err.message : 'Invalid email or password';
      const display = msg.toLowerCase().includes('session') ? 'Invalid email or password' : msg;
      toast.error(display);
      setIsLoading(false);
    }
  };

  const handleTwoFactorVerify = async (code: string): Promise<void> => {
    setTwoFactorBusy(true);
    setTwoFactorError(null);
    try {
      await attemptLogin(code);
      setNeedsTwoFactor(false);
    } catch (err) {
      if (isTwoFactorRequired(err)) {
        setTwoFactorError(t('login.twoFactor.required'));
      } else {
        // 401 from a wrong code is remapped to a session message by the API
        // client — show a clear 2FA-specific inline error instead.
        setTwoFactorError(t('login.twoFactor.invalid'));
      }
      setTwoFactorBusy(false);
    }
  };

  const handleTwoFactorCancel = (): void => {
    if (twoFactorBusy) return;
    setNeedsTwoFactor(false);
    setTwoFactorError(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    clearFieldError(name as LoginField);
  };

  return (
    <AuthPageLayout>
      <TwoFactorVerifyModal
        open={needsTwoFactor}
        busy={twoFactorBusy}
        error={twoFactorError}
        onVerify={(code) => { void handleTwoFactorVerify(code); }}
        onCancel={handleTwoFactorCancel}
      />
      <AuthFormCard>
        <div className="mb-8 text-center">
          <h1 className="mb-2 text-2xl font-bold tracking-tight text-gray-900">Welcome back</h1>
          <p className="text-sm text-gray-500">Sign in to your account to continue.</p>
        </div>

        <ShopifyReconnectNotice redirectTo={redirectTo} />

        <form onSubmit={handleSubmit} className="space-y-5" noValidate>
          <div className="space-y-2">
            <AuthLabel htmlFor="email" required>Email</AuthLabel>
            <AuthInput
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="you@example.com"
              disabled={isLoading}
              error={!!errors.email}
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? 'email-error' : undefined}
            />
            <FieldError id="email-error" message={errors.email} />
          </div>

          <div className="space-y-2">
            <AuthLabel htmlFor="password" required>Password</AuthLabel>
            <div className="relative">
              <AuthInput
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Enter your password"
                disabled={isLoading}
                className="pr-10"
                error={!!errors.password}
                aria-invalid={!!errors.password}
                aria-describedby={errors.password ? 'password-error' : undefined}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                disabled={isLoading}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <FieldError id="password-error" message={errors.password} />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AuthCheckbox
                id="rememberMe"
                checked={formData.rememberMe}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({
                    ...prev,
                    rememberMe: checked,
                  }))
                }
                disabled={isLoading}
              />
              <AuthLabel
                htmlFor="rememberMe"
                className="cursor-pointer text-sm font-normal text-gray-700"
              >
                Remember me
              </AuthLabel>
            </div>
            <Link
              to="/forgot-password"
              className="text-sm font-medium text-gray-900 hover:text-gray-600"
            >
              Forgot password?
            </Link>
          </div>

          <AuthButton type="submit" disabled={isLoading} loading={isLoading} className="w-full">
            {isLoading ? 'Signing in...' : 'Sign in'}
          </AuthButton>
        </form>

        <AuthDivider />

        <AuthGoogleButton
          onClick={() => {
            if (isNativePlatform()) {
              void startNativeGoogleLogin();
            } else {
              rememberShopifyHandoff(shopifyShop);
              rememberGoogleSignInRedirect(redirectParam);
              void startGoogleSignIn();
            }
          }}
        />

        {nativePlatform() === 'ios' && (
          <button
            type="button"
            onClick={async () => {
              const r = await startNativeAppleLogin();
              if (r.ok) {
                toast.success('Signed in successfully.');
                startTransition(() => { navigate(redirectTo); });
              } else if (r.error !== 'unsupported') {
                toast.error('Apple sign-in failed. Please try again.');
              }
            }}
            className="mt-3 inline-flex h-11 w-full items-center justify-center gap-3 rounded-lg border border-black bg-black px-4 text-sm font-medium text-white transition-colors hover:bg-gray-900"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M16.37 1.43c0 1.14-.42 2.22-1.18 3.02-.82.88-2.16 1.56-3.28 1.47-.13-1.09.43-2.25 1.15-3.01.81-.86 2.24-1.5 3.31-1.48zM20.9 17.1c-.55 1.27-.82 1.84-1.53 2.97-1 1.58-2.4 3.55-4.14 3.56-1.55.02-1.95-1.01-4.05-1-2.1.01-2.54 1.02-4.09 1.01-1.74-.01-3.07-1.79-4.06-3.37-2.78-4.41-3.08-9.59-1.36-12.34 1.22-1.95 3.15-3.1 4.96-3.1 1.85 0 3.01 1.01 4.54 1.01 1.48 0 2.39-1.01 4.53-1.01 1.61 0 3.32.88 4.54 2.4-3.99 2.18-3.34 7.87.21 9.86z" />
            </svg>
            Sign in with Apple
          </button>
        )}

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            {t('login.noAccount')}{' '}
            <Link
              to={shopifyShop ? `/signup${window.location.search}` : '/signup'}
              className="font-semibold text-gray-900 underline underline-offset-2 hover:text-gray-600"
            >
              {t('login.createAccount')}
            </Link>
          </p>
        </div>
      </AuthFormCard>
    </AuthPageLayout>
  );
};

export default Login;
